import { useState } from 'react';

import Menu from 'components/global/Menu';
import Typography from 'components/global/Typography';

import useAlert from 'hooks/useAlert';

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faCopy, faEnvelope, faShare, faShareNodes } from '@fortawesome/pro-regular-svg-icons';

import Button from './Button';
import * as S from './styled/CopyComponent.styled';

/**
 * @author Burak Ak
 * @description This component is used to copy text to clipboard
 * @param {Object} props - props of the typography component
 * @param {string} [props.value] - The value of the input.
 * @param {boolean} [props.showShareOnly] - If true, the component will show only the share button.
 * @param {string} [props.label] - The label of the share button.
 * @returns {object} - Text object with the given props
 * @example
	<CopyComponent
		value="$2a$12$95YVMJ.VbZOk3T6lmAxaqeg8r4UTXErYlYv94JGv1p6fLckw/SDR6"
	/>
*
 */

const CopyComponent = ({ value, share, toEmail, buttonDisabled, className, disabledTooltip, showShareOnly, label }) => {
	const [shareMenuButtonRef, setShareMenuButtonRef] = useState(null);
	const { showInfo } = useAlert();

	const shareMenuItems = [
		...(showShareOnly ? [{ label: 'copy', icon: faCopy }] : []),
		{
			label: 'email',
			url: toEmail ? `mailto:${toEmail}?subject=Share&body=` : 'mailto:?body=',
			icon: faEnvelope
		},
		{
			label: 'WhatsApp',
			url: 'https://wa.me/?text=',
			icon: faWhatsapp
		}
	];

	const copyToClipboard = () => {
		showInfo({
			header: 'copyComponentHeader',
			message: 'copyComponentMessage'
		});
		navigator.clipboard.writeText(value);
	};

	return (
		<>
			{showShareOnly ? (
				<Button
					variant="blueBorderless"
					ref={setShareMenuButtonRef}
					icon={faShareNodes}
					iconSize="small"
					label={label}
				/>
			) : (
				<S.Container className={className ?? ''} onClick={copyToClipboard}>
					<Typography showTooltip={false}>{value ?? '-'}</Typography>

					<div>
						<Button
							disabled={!value || buttonDisabled}
							disabledTooltip={disabledTooltip}
							variant="blueBorderless"
							icon={faCopy}
							iconSize="small"
							iconButton
						/>

						{share && (
							<Button
								disabled={!value}
								variant="blueBorderless"
								ref={setShareMenuButtonRef}
								icon={faShare}
								iconSize="small"
								iconButton
							/>
						)}
					</div>
				</S.Container>
			)}
			<Menu
				placement="right-start"
				buttonRef={shareMenuButtonRef}
				hoverMode={false}
				menuItems={shareMenuItems.map((language) => ({
					label: language.label,
					icon: language.icon,
					action: () => {
						if (language.label === 'copy') {
							copyToClipboard();
						} else {
							window.open(language.url + value, '_self');
						}
					}
				}))}
			/>
		</>
	);
};

export default CopyComponent;
